import { log } from '../utils/logger';
import {
  ColumnType,
  CommentMode,
  RecipeColumnState,
  RecipeGroupState,
  RecipeRowState,
  RecipeState,
  RecipeTemplateDto,
  RowType,
  Source,
  Visibility,
} from '@/components/recipe/types';
import {
  RecipeDto,
  RecipeGroupDto,
  RecipeRowDto,
  RecipeTemplateColumnDto,
  RecipeTemplateGroupDto,
  RecipeTemplateRowDto,
  SampleDto,
} from '@/api/ApiClient';

/**
 * Extract a value from a startingMaterial sample using a field path.
 * Handles nested fields (e.g., "substrate.name") and arrays (joins with ', ').
 * Special handling for "materials" field: uses GrowthMaterials (materials) if available,
 * otherwise falls back to substrateMaterials (same behavior as sample detail).
 *
 * @param startingMaterial The startingMaterial sample to extract from
 * @param fieldPath The field path to extract (e.g., "sampleLabel", "materials", "substrate.name")
 * @returns The extracted value, or undefined if not found
 */
function getStartingMaterialValue(startingMaterial: SampleDto, fieldPath: string): any {
  // Special handling for "materials" field: check materials first, then substrateMaterials
  // This matches the behavior in SampleDetails.tsx where GrowthMaterials (materials) is preferred
  // over substrateMaterials
  if (fieldPath === 'materials') {
    const materials =
      startingMaterial.materials && startingMaterial.materials.length > 0
        ? startingMaterial.materials
        : startingMaterial.substrateMaterials && startingMaterial.substrateMaterials.length > 0
          ? startingMaterial.substrateMaterials
          : undefined;

    if (materials !== undefined) {
      return materials.join(', ');
    }
    return undefined;
  }

  const fields = fieldPath.split('.');
  let value: any = startingMaterial;

  // Navigate the field path
  for (const field of fields) {
    value = value?.[field];
    if (value === undefined || value === null) {
      return undefined;
    }
  }

  // Handle arrays - join with comma separator
  if (Array.isArray(value)) {
    return value.join(', ');
  }

  return value;
}

/**
 * Create a row populated from startingMaterial sample data.
 * Used when a group has RowType.StartingMaterials.
 *
 * @param groupTemplate The group template definition
 * @param startingMaterial The startingMaterial sample to populate from
 * @param startingMaterialIndex The 1-based index of this startingMaterial
 * @returns A RecipeRowState with values populated from the startingMaterial
 */
function createStartingMaterialRow(
  groupTemplate: RecipeTemplateGroupDto,
  startingMaterial: SampleDto,
  startingMaterialIndex: number,
  rowId?: number
): RecipeRowState {
  const initialValues = new Map<string, any>();

  // Populate values from startingMaterial for Source.StartingMaterial columns
  groupTemplate.columns?.forEach((col) => {
    const columnSymbol = col.symbol || col.name || '';

    // Skip columns with no symbol/name (shouldn't happen, but be safe)
    if (!columnSymbol) {
      log.warn('[RecipeMapper] Column without symbol or name found in startingMaterial group');
      return;
    }

    if (col.sourceType === Source.StartingMaterial && col.source) {
      // Determine which startingMaterial to use
      // If column has startingMaterialIndex, use that (fixed reference)
      // Otherwise use the row's startingMaterial (dynamic binding)
      const targetStartingMaterialIndex = (col as any).startingMaterialIndex || startingMaterialIndex;

      // For now, we only have access to this startingMaterial
      // If we need a different one, we'd need to pass all startingMaterials
      // For dynamic binding (RowType.StartingMaterials), targetStartingMaterialIndex === startingMaterialIndex
      if (targetStartingMaterialIndex === startingMaterialIndex) {
        const value = getStartingMaterialValue(startingMaterial, col.source);
        if (value !== undefined && value !== null && value !== '') {
          initialValues.set(columnSymbol, value);
        } else {
          // Set empty value so the cell doesn't show undefined
          initialValues.set(columnSymbol, '');
        }
      } else {
        // Different startingMaterial needed - leave empty for now
        initialValues.set(columnSymbol, '');
      }
    } else if (col.defaultValue !== undefined && col.defaultValue !== null) {
      // Set default values for non-startingMaterial columns
      if (col.type === ColumnType.DateTime) {
        const defaultVal =
          col.defaultValue === 'now'
            ? new Date()
                .toLocaleString('en-US', {
                  year: 'numeric',
                  month: '2-digit',
                  day: '2-digit',
                  hour: '2-digit',
                  minute: '2-digit',
                  hour12: true,
                })
                .replace(',', '')
            : col.defaultValue;
        initialValues.set(columnSymbol, defaultVal);
      } else {
        initialValues.set(columnSymbol, col.defaultValue);
      }
    } else {
      // Set appropriate empty value based on column type for ALL other columns
      // This ensures every column has a value, preventing undefined keys
      if (col.type === ColumnType.Number || col.type === ColumnType.Scientific) {
        initialValues.set(columnSymbol, '');
      } else if (col.type === ColumnType.MultiSelect) {
        initialValues.set(columnSymbol, []);
      } else {
        initialValues.set(columnSymbol, '');
      }
    }
  });

  return {
    id: rowId ?? startingMaterialIndex + 1, // Use provided ID or fallback to startingMaterialIndex + 1
    symbol: `startingMaterial_${startingMaterialIndex}`,
    name: `StartingMaterial ${startingMaterialIndex}`,
    comment: '',
    definedInTemplate: false, // Dynamically created
    values: initialValues,
    showComment: false,
    scrollTo: false,
    startingMaterialIndex: startingMaterialIndex, // Track which startingMaterial this row represents
  };
}

/**
 * Maps RecipeDto to RecipeState for use in the Recipe component
 */
export function mapRecipeDtoToState(recipe: RecipeDto): RecipeState {
  return {
    id: recipe.id,
    templateName: recipe.templateName || '',
    templateHash: recipe.templateHash,
    groups: recipe.groups?.map((group) => mapGroupDto(group)) || [],
    calculatedRuntime: recipe.calculatedRuntime,
    runtimeChangedManually: recipe.runtimeChangedManualy || false,
  };
}

/**
 * Maps RecipeTemplateDto to RecipeState for initialization from template
 * This is the main mapping function used when creating a new recipe from a template
 *
 * @param template The recipe template to convert
 * @param sampleStartingMaterials Optional array of startingMaterial samples for RowType.StartingMaterials groups
 */
export function mapTemplateToRecipeState(
  template: RecipeTemplateDto,
  sampleStartingMaterials?: SampleDto[]
): RecipeState {
  // If template has no groups, it's defective - return empty state
  if (!template.groups || template.groups.length === 0) {
    return {
      templateName: template.name || '',
      groups: [],
      calculatedRuntime: undefined,
      runtimeChangedManually: false,
    };
  }

  // Track global row ID counter to ensure unique IDs across all groups
  let nextRowId = 1;

  // Map all groups, ensuring row IDs are unique globally
  const groups = template.groups.map((groupTemplate) =>
    mapTemplateGroup(groupTemplate, sampleStartingMaterials, () => nextRowId++)
  );

  return {
    templateName: template.name || '',
    groups: groups,
    calculatedRuntime: undefined,
    runtimeChangedManually: false,
  };
}

/**
 * Maps RecipeState back to RecipeDto for saving to backend
 */
export function mapRecipeStateToDto(data: RecipeState): RecipeDto {
  return {
    id: data.id,
    templateName: data.templateName,
    templateHash: data.templateHash,
    calculatedRuntime: data.calculatedRuntime,
    runtimeChangedManualy: data.runtimeChangedManually,
    groups: data.groups.map((group) => mapGroupState(group)),
  };
}

/**
 * Creates a RecipeTemplateDto from an existing recipe structure
 */
export function createTemplateFromRecipe(recipe: RecipeDto, activityType?: string): RecipeTemplateDto {
  const template: RecipeTemplateDto = {
    id: recipe.id || 1,
    name: recipe.templateName || 'Recipe Template',
    definesMaterial: true,
    usesInstrument: true,
    // Use recipe's actual runtime calculation if available, otherwise omit
    calcRuntime: (recipe as any).calcRuntime,
    calcRuntimeFormula: (recipe as any).calcRuntimeFormula,
    runtimeUnit: 'minutes',
    techniques: [],
    step: activityType || 'Processing',
    instrumentTypes: [],
    instruments: [],
    formulaConstants: {},
    groups: recipe.groups?.map((group) => mapGroupToTemplate(group)) || [],
  };
  log.debug('createTemplateFromRecipe:', template);
  return template;
}

function mapGroupToTemplate(group: RecipeGroupDto): RecipeTemplateGroupDto {
  return {
    id: group.id || 1,
    name: group.name || 'Group',
    color: group.color || '#f5f5f5',
    symbol: group.symbol || 'group',
    rowType: (group.rowType as any) || RowType.Template,
    scrolling: group.scrolling || false,
    allowSeveral: group.allowSeveral || false,
    copyLastRow: group.copyLastRow || false,
    comments: (group.comments as any) || CommentMode.OFF,
    columns: group.columns || [],
    rows:
      group.rows
        ?.filter((row) => row.definedInTemplate) // Only include rows defined in the template, the rest is user data.
        .map((row) => ({
          id: row.id || 1,
          symbol: row.symbol || 'row',
          name: row.name || 'Row',
          values: row.values?.map(([key, value]) => [key, value]) || [],
          formulas: row.formulas || [],
        })) || [],
  };
}

function mapRowStateToTemplate(row: RecipeRowState): RecipeTemplateRowDto {
  return {
    id: row.id || 1,
    symbol: row.symbol || 'row',
    name: row.name || 'Row',
    description: row.description,
    values: Array.from(row.values.entries()).map(([key, value]) => [key, String(value)] as [string, string]),
    formulas: row.formulas || [],
  };
}

/**
 * Maps RecipeGroupState (runtime data) to RecipeTemplateGroupDto for dependency analysis
 * Used as fallback when template.groups are missing but recipeState.groups are available
 */
export function mapRecipeGroupStateToTemplateGroup(group: RecipeGroupState): RecipeTemplateGroupDto {
  return {
    id: group.id,
    name: group.name,
    color: group.color,
    symbol: group.symbol,
    rowType: group.rowType as any,
    scrolling: group.scrolling,
    allowSeveral: group.allowSeveral,
    copyLastRow: group.copyLastRow,
    comments: group.comments as any,
    columns: group.columns.map((col) => mapColumnState(col)),
    rows: group.rows
      .filter((row) => row.definedInTemplate) // Only include rows defined in the template, the rest is user data.
      .map((row) => mapRowStateToTemplate(row)),
  };
}

function mapGroupState(group: RecipeGroupState): RecipeGroupDto {
  return {
    id: group.id,
    name: group.name,
    color: group.color,
    symbol: group.symbol,
    rowType: group.rowType,
    scrolling: group.scrolling,
    allowSeveral: group.allowSeveral,
    copyLastRow: group.copyLastRow,
    comments: group.comments,
    columns: group.columns.map((col) => mapColumnState(col)),
    rows: group.rows.map((row) => mapRowState(row)),
  };
}

function mapRowState(row: RecipeRowState): RecipeRowDto {
  // In the original implementation, if comment is "End repeat", set endRepeat flag
  const endRepeat = row.comment === 'End repeat';

  return {
    id: row.id,
    symbol: row.symbol,
    name: row.name,
    description: row.description,
    comment: row.comment,
    repeat: row.repeat,
    endRepeat: endRepeat,
    definedInTemplate: row.definedInTemplate,
    calcResultModified: row.calcResultModified,
    formulas: row.formulas,
    values: Array.from(row.values.entries()),
    dropdownValues: row.dropdownValues,
  };
}

function mapColumnState(col: RecipeColumnState): RecipeTemplateColumnDto {
  return {
    id: col.id,
    symbol: col.symbol,
    name: col.name,
    unit: col.unit,
    type: col.type,
    defaultValue: col.defaultValue,
    precision: col.precision,
    sourceType: col.sourceType,
    source: col.source,
    visibility: col.visibility,
    visibilityFormula: col.visibilityFormula,
    refColumn: col.refColumn,
    visibilityOp: col.visibilityOp,
    visibilityVal: col.visibilityVal,
    updateVisibilities: col.updateVisibilities,
    values: col.values,
    lookupValues: col.lookupValues,
    color: col.color,
    width: col.width,
  };
}

function mapGroupDto(group: RecipeGroupDto): RecipeGroupState {
  const rowType = (group.rowType as any) || RowType.Template;
  return {
    id: group.id,
    name: group.name || '',
    color: group.color || '#f5f5f5',
    symbol: group.symbol || '',
    rowType: rowType,
    scrolling: group.scrolling || false,
    allowSeveral: group.allowSeveral || false,
    copyLastRow: group.copyLastRow || false,
    comments: (group.comments as any) || CommentMode.OFF,
    regexFirstInclude: undefined,
    regexFirstExclude: undefined,
    filterColumn: undefined,
    columns: group.columns?.map((col) => mapTemplateCol(col)) || [],
    rows: group.rows?.map((row) => mapRowDto(row, group.id)) || [],
    // Port from Knockout: canAddRow checks rowType !== One && rowType !== Template
    canAddGroup: group.allowSeveral || (rowType !== RowType.One && rowType !== RowType.Template),
    canRemoveGroup: false,
    commentColSpan: 1,
    showAllComments: false,
  };
}

/**
 * Maps a recipe template column to column state
 *
 * NOTE: This function does NOT populate dynamic lookup values (col.lookupValues).
 * Dynamic lookup population is a LEGACY feature that should eventually be removed.
 * For now, it's handled at the component level when the template is first set.
 * See RecipeActivityDetail.tsx for the dynamic lookup implementation.
 *
 * Legacy lookup types:
 * - "substrate_CMP" (RawMaterialCompounding) - filters bulk materials by compounding techniques
 * - "substrate_CVT" (RawMaterialTransport) - filters bulk materials by CVT techniques
 * - "substrate_FZ" (RawMaterialFloatingZone) - filters bulk materials by floating zone techniques
 */
function mapTemplateCol(col: RecipeTemplateColumnDto): RecipeColumnState {
  const mappedCol = {
    id: col.id,
    symbol: col.symbol || '',
    name: col.name || '',
    unit: col.unit || '',
    type: (col.type as any) || ColumnType.Text,
    defaultValue: col.defaultValue,
    precision: col.precision,
    sourceType: (col.sourceType as any) || Source.Manual,
    source: col.source,
    visibility: (col.visibility as any) || Visibility.Always,
    visibilityFormula: col.visibilityFormula,
    refColumn: col.refColumn,
    visibilityOp: col.visibilityOp as any,
    visibilityVal: col.visibilityVal,
    updateVisibilities: col.updateVisibilities,
    values: col.values,
    lookupValues: col.lookupValues, // LEGACY: Used for dynamic lookup population
    color: col.color,
    width: col.width,
    startingMaterialIndex: (col as any).startingMaterialIndex, // For Source.StartingMaterial columns
    visible: col.visibility !== Visibility.Never && col.visibility !== Visibility.OnlyForUpload,
    // Mark startingMaterial columns as readonly (they're auto-populated from sample data)
    readonly:
      col.sourceType === Source.Formula ||
      col.sourceType === Source.Constant ||
      col.sourceType === Source.Autoincrement ||
      col.sourceType === Source.StartingMaterial,
    isNumber: col.type === ColumnType.Number,
    isScientific: col.type === ColumnType.Scientific,
    isText: col.type === ColumnType.Text,
    isHTML: col.type === ColumnType.HTML,
    isDateTime: col.type === ColumnType.DateTime,
    isList: col.type === ColumnType.List,
    isMultiSelect: col.type === ColumnType.MultiSelect,
    isOptionalList: col.type === ColumnType.OptionalList,
    isExtendibleList: col.type === ColumnType.ExtendibleList,
    isLookup: col.type === ColumnType.Lookup,
    useOtherOption: col.type === ColumnType.ExtendibleList,
    allowClear: col.type === ColumnType.OptionalList,
    dropdownValues: col.values || [],
  };

  return mappedCol;
}

/**
 * Maps RecipeRowDto (from server) to RecipeRowState
 * This preserves all row properties including repeat/endRepeat
 */
function mapRowDto(row: RecipeRowDto, groupId?: number): RecipeRowState {
  // In the original implementation, endRepeat is displayed as a comment "End repeat"
  const comment = row.comment || (row.endRepeat ? 'End repeat' : '');
  const showComment = Boolean(comment);

  return {
    id: row.id,
    symbol: row.symbol || '',
    name: row.name || '',
    description: row.description,
    comment: comment,
    repeat: row.repeat,
    endRepeat: row.endRepeat,
    definedInTemplate: row.definedInTemplate || false,
    calcResultModified: row.calcResultModified || [],
    formulas: row.formulas,
    values: new Map(row.values?.map(([key, value]) => [key, value]) || []),
    dropdownValues: row.dropdownValues,
    recipeGroupID: groupId,
    showComment: showComment,
    scrollTo: false,
  };
}

/**
 * Creates an empty row for groups that allow adding rows but have no predefined rows
 * Also populates startingMaterial values if columns have startingMaterialIndex specified
 */
function createEmptyRow(
  groupTemplate: RecipeTemplateGroupDto,
  sampleStartingMaterials?: SampleDto[],
  rowId?: number
): RecipeRowState {
  // Initialize values map with empty values for all columns
  const initialValues = new Map<string, string | number | string[]>();

  // Initialize all columns with empty values or startingMaterial values
  groupTemplate.columns?.forEach((col) => {
    const columnSymbol = col.symbol || col.name || '';

    // Handle Source.StartingMaterial columns with fixed startingMaterialIndex
    if (col.sourceType === Source.StartingMaterial && col.source && (col as any).startingMaterialIndex) {
      const startingMaterialIndex = (col as any).startingMaterialIndex;
      if (sampleStartingMaterials && sampleStartingMaterials[startingMaterialIndex - 1]) {
        const value = getStartingMaterialValue(sampleStartingMaterials[startingMaterialIndex - 1], col.source);
        if (value !== undefined && value !== null && value !== '') {
          initialValues.set(columnSymbol, value);
        } else {
          initialValues.set(columnSymbol, '');
        }
      } else {
        // StartingMaterial doesn't exist
        initialValues.set(columnSymbol, '');
      }
    } else if (col.defaultValue !== undefined && col.defaultValue !== null) {
      // Port from Knockout: Format DateTime default values (especially "now")
      // Matches RecipeRowVM._getDefaultValue()
      if (col.type === ColumnType.DateTime) {
        const defaultVal =
          col.defaultValue === 'now'
            ? new Date()
                .toLocaleString('en-US', {
                  year: 'numeric',
                  month: '2-digit',
                  day: '2-digit',
                  hour: '2-digit',
                  minute: '2-digit',
                  hour12: true,
                })
                .replace(',', '')
            : col.defaultValue;
        initialValues.set(col.symbol || col.name || '', defaultVal);
      } else {
        initialValues.set(col.symbol || col.name || '', col.defaultValue);
      }
    } else {
      // Set appropriate empty value based on column type
      if (col.type === ColumnType.Number || col.type === ColumnType.Scientific) {
        initialValues.set(col.symbol || col.name || '', '');
      } else if (col.type === ColumnType.MultiSelect) {
        initialValues.set(col.symbol || col.name || '', []);
      } else {
        initialValues.set(col.symbol || col.name || '', '');
      }
    }
  });

  return {
    id: rowId ?? 1, // Use provided ID or fallback to 1
    symbol: '',
    name: '',
    comment: '',
    definedInTemplate: false,
    values: initialValues,
    showComment: false,
    scrollTo: false,
  };
}

function mapTemplateGroup(
  groupTemplate: RecipeTemplateGroupDto,
  sampleStartingMaterials?: SampleDto[],
  getNextRowId?: () => number
): RecipeGroupState {
  const rowType = (groupTemplate.rowType as any) || RowType.Template;

  // Handle RowType.StartingMaterials - create rows from sample startingMaterials
  if (rowType === RowType.StartingMaterials) {
    if (!sampleStartingMaterials || sampleStartingMaterials.length === 0) {
      // Return group with no rows - will show empty table
      return {
        id: groupTemplate.id,
        name: groupTemplate.name || '',
        color: groupTemplate.color || '#f5f5f5',
        symbol: groupTemplate.symbol || '',
        rowType: rowType,
        scrolling: groupTemplate.scrolling || false,
        allowSeveral: false,
        copyLastRow: false,
        comments: (groupTemplate.comments as any) || CommentMode.OFF,
        regexFirstInclude: groupTemplate.regexFirstInclude,
        regexFirstExclude: groupTemplate.regexFirstExclude,
        filterColumn: groupTemplate.filterColumn,
        columns: groupTemplate.columns?.map((col) => mapTemplateCol(col)) || [],
        rows: [],
        canAddGroup: false, // Can't manually add startingMaterial rows
        canRemoveGroup: false,
        commentColSpan: 1,
        showAllComments: false,
      };
    }

    // Create one row per startingMaterial with unique IDs
    const rows = sampleStartingMaterials.map((startingMaterial, index) =>
      createStartingMaterialRow(groupTemplate, startingMaterial, index + 1, getNextRowId ? getNextRowId() : index + 1)
    );

    return {
      id: groupTemplate.id,
      name: groupTemplate.name || '',
      color: groupTemplate.color || '#f5f5f5',
      symbol: groupTemplate.symbol || '',
      rowType: rowType,
      scrolling: groupTemplate.scrolling || false,
      allowSeveral: false,
      copyLastRow: false,
      comments: (groupTemplate.comments as any) || CommentMode.OFF,
      regexFirstInclude: groupTemplate.regexFirstInclude,
      regexFirstExclude: groupTemplate.regexFirstExclude,
      filterColumn: groupTemplate.filterColumn,
      columns: groupTemplate.columns?.map((col) => mapTemplateCol(col)) || [],
      rows: rows,
      canAddGroup: false, // Can't manually add startingMaterial rows
      canRemoveGroup: false,
      commentColSpan: 1,
      showAllComments: false,
    };
  }

  // Handle other row types (existing logic)
  const templateRows =
    groupTemplate.rows?.map((rowTemplate) => mapTemplateRowFromTemplate(groupTemplate, rowTemplate, getNextRowId)) ||
    [];

  // If the group has no predefined rows, create initial row(s)
  let rows = templateRows;
  if (templateRows.length === 0) {
    // RowType.One always needs exactly one row
    // RowType.Manual and Fileupload need at least one row to start
    if (rowType === RowType.One || rowType === RowType.Manual || rowType === RowType.Fileupload) {
      rows = [createEmptyRow(groupTemplate, sampleStartingMaterials, getNextRowId ? getNextRowId() : 1)];
    }
  }

  return {
    id: groupTemplate.id,
    name: groupTemplate.name || '',
    color: groupTemplate.color || '#f5f5f5',
    symbol: groupTemplate.symbol || '',
    rowType: rowType,
    scrolling: groupTemplate.scrolling || false,
    allowSeveral: groupTemplate.allowSeveral || false,
    copyLastRow: groupTemplate.copyLastRow || false,
    comments: (groupTemplate.comments as any) || CommentMode.OFF,
    regexFirstInclude: groupTemplate.regexFirstInclude,
    regexFirstExclude: groupTemplate.regexFirstExclude,
    filterColumn: groupTemplate.filterColumn,
    columns: groupTemplate.columns?.map((col) => mapTemplateCol(col)) || [],
    rows: rows,
    // Port from Knockout: canAddRow checks rowType !== One && rowType !== Template && rowType !== StartingMaterials
    canAddGroup:
      groupTemplate.allowSeveral ||
      (rowType !== RowType.One && rowType !== RowType.Template && rowType !== RowType.StartingMaterials),
    canRemoveGroup: false, // Groups themselves can't be removed
    commentColSpan: 1,
    showAllComments: false,
  };
}

function mapTemplateRowFromTemplate(
  groupTemplate: RecipeTemplateGroupDto,
  rowTemplate: RecipeTemplateRowDto,
  getNextRowId?: () => number
): RecipeRowState {
  // Initialize values map with template row values
  const initialValues = new Map(rowTemplate.values?.map(([key, value]) => [key, value]) || []);

  // Initialize all columns with empty values
  groupTemplate.columns?.forEach((col) => {
    if (col.symbol && !initialValues.has(col.symbol)) {
      initialValues.set(col.symbol, '');
    }
  });

  // Map template row attributes to columns with matching symbols (for backward compatibility)
  if (rowTemplate.name) {
    const nameColumn = groupTemplate.columns?.find((col) => col.symbol === 'name');
    if (nameColumn) {
      initialValues.set('name', rowTemplate.name);
    }
  }

  if (rowTemplate.symbol) {
    const symbolColumn = groupTemplate.columns?.find((col) => col.symbol === 'symbol');
    if (symbolColumn) {
      initialValues.set('symbol', rowTemplate.symbol);
    }
  }

  // Use template ID if available, otherwise get next unique ID globally, or fallback to 1
  const rowId = rowTemplate.id ?? (getNextRowId ? getNextRowId() : 1);

  return {
    id: rowId,
    symbol: rowTemplate.symbol || '',
    name: rowTemplate.name || '',
    description: rowTemplate.description,
    comment: '',
    repeat: undefined,
    endRepeat: false,
    definedInTemplate: true,
    calcResultModified: [],
    formulas: rowTemplate.formulas,
    values: initialValues,
    dropdownValues: rowTemplate.dropdownValues,
    recipeGroupID: groupTemplate.id,
    showComment: false,
    scrollTo: false,
  };
}
